import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { 
  Play, Pause, Users, Copy, Check, Crown, Settings, 
  RotateCcw, Square, Volume2, VolumeX, Loader2, TrendingUp, Hash, Music, Ticket, Printer
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';

// UK Bingo number calls (traditional calls)
const UK_BINGO_CALLS = {
  1: "Kelly's Eye",
  2: "One Little Duck",
  3: "Cup of Tea",
  4: "Knock at the Door",
  5: "Man Alive",
  6: "Tom Mix",
  7: "Lucky Seven",
  8: "Garden Gate",
  9: "Doctor's Orders",
  10: "Boris's Den",
  11: "Legs Eleven",
  12: "One Dozen",
  13: "Unlucky for Some",
  14: "Valentine's Day",
  15: "Young and Keen",
  16: "Sweet Sixteen",
  17: "Dancing Queen",
  18: "Coming of Age",
  19: "Goodbye Teens",
  20: "One Score",
  21: "Royal Salute",
  22: "Two Little Ducks",
  23: "Thee and Me",
  24: "Two Dozen",
  25: "Duck and Dive",
  26: "Pick and Mix",
  27: "Gateway to Heaven",
  28: "Over Weight",
  29: "Rise and Shine",
  30: "Dirty Gertie",
  31: "Get Up and Run",
  32: "Buckle My Shoe",
  33: "All the Threes",
  34: "Ask for More",
  35: "Jump and Jive",
  36: "Three Dozen",
  37: "More than Eleven",
  38: "Christmas Cake",
  39: "Steps",
  40: "Naughty Forty",
  41: "Time for Fun",
  42: "Winnie the Pooh",
  43: "Down on Your Knees",
  44: "Droopy Drawers",
  45: "Halfway There",
  46: "Up to Tricks",
  47: "Four and Seven",
  48: "Four Dozen",
  49: "PC",
  50: "Half a Century",
  51: "Tweak of the Thumb",
  52: "Danny La Rue",
  53: "Here Comes Herbie",
  54: "Clean the Floor",
  55: "All the Fives",
  56: "Was She Worth It?",
  57: "Heinz Varieties",
  58: "Make Them Wait",
  59: "Brighton Line",
  60: "Five Dozen",
  61: "Baker's Bun",
  62: "Turn the Screw",
  63: "Tickle Me",
  64: "Red Raw",
  65: "Old Age Pension",
  66: "Clickety Click",
  67: "Made in Heaven",
  68: "Saving Grace",
  69: "Either Way Up",
  70: "Three Score and Ten",
  71: "Bang on the Drum",
  72: "Six Dozen",
  73: "Queen Bee",
  74: "Candy Store",
  75: "Strive and Strive",
  76: "Trombones",
  77: "Sunset Strip",
  78: "Heaven's Gate",
  79: "One More Time",
  80: "Eight and Blank",
  81: "Stop and Run",
  82: "Straight On Through",
  83: "Time for Tea",
  84: "Seven Dozen",
  85: "Staying Alive",
  86: "Between the Sticks",
  87: "Torquay in Devon",
  88: "Two Fat Ladies",
  89: "Nearly There",
  90: "Top of the Shop"
};

export default function BingoUK90HostPage() {
  const { gameCode } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [game, setGame] = useState(null);
  const [hostName, setHostName] = useState('');
  const [winPatterns, setWinPatterns] = useState(['one_line']); // Multiple patterns
  const [ticketCount, setTicketCount] = useState(1); // Tickets per player (1-6)
  const [autoCallInterval, setAutoCallInterval] = useState(15);
  const [clubId, setClubId] = useState(searchParams.get('club') || '');
  const [clubs, setClubs] = useState([]);
  const [userClubs, setUserClubs] = useState([]); // Clubs owned by the current user
  const [creating, setCreating] = useState(false);
  const [calling, setCalling] = useState(false);
  const [autoCallActive, setAutoCallActive] = useState(false);
  const [copied, setCopied] = useState(false);
  const [lastCalledNumber, setLastCalledNumber] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(false); // Default OFF

  // Fetch user's owned clubs (for club-specific hosting)
  useEffect(() => {
    const fetchUserClubs = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const userData = await res.json();
          // Fetch clubs owned by this user that have bingo enabled
          const clubsRes = await fetch(`${API_URL}/api/clubs`);
          if (clubsRes.ok) {
            const allClubs = await clubsRes.json();
            const ownedClubs = allClubs.filter(c => 
              c.owner_id === userData.user_id && c.bingo_enabled
            );
            setUserClubs(ownedClubs);
            
            // If user owns clubs with bingo enabled and no club pre-selected, auto-select the first one
            if (ownedClubs.length > 0 && !searchParams.get('club')) {
              setClubId(ownedClubs[0].club_id);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user clubs:', error);
      }
    };
    fetchUserClubs();
  }, [searchParams]);

  // Fetch all clubs for dropdown (admin mode or when user has no clubs)
  useEffect(() => {
    const fetchClubs = async () => {
      try {
        const res = await fetch(`${API_URL}/api/clubs`);
        if (res.ok) {
          const allClubs = await res.json();
          // Only show clubs with bingo enabled
          const bingoEnabledClubs = allClubs.filter(c => c.bingo_enabled);
          setClubs(bingoEnabledClubs);
        }
      } catch (error) {
        console.error('Error fetching clubs:', error);
      }
    };
    fetchClubs();
  }, []);

  // Fetch game state
  const fetchGame = useCallback(async () => {
    if (!gameCode) return;
    
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${gameCode}`);
      if (res.ok) {
        const data = await res.json();
        setGame(data);
        if (data.called_numbers?.length > 0) {
          setLastCalledNumber(data.called_numbers[data.called_numbers.length - 1]);
        }
      }
    } catch (error) {
      console.error('Error fetching game:', error);
    }
  }, [gameCode]);

  useEffect(() => {
    if (gameCode) {
      fetchGame();
      const interval = setInterval(fetchGame, 2000);
      return () => clearInterval(interval);
    }
  }, [gameCode, fetchGame]);

  // Auto-call effect
  useEffect(() => {
    if (!autoCallActive || !game || game.status !== 'active') return;
    
    const interval = setInterval(() => {
      callNumber();
    }, autoCallInterval * 1000);
    
    return () => clearInterval(interval);
  }, [autoCallActive, game?.status, autoCallInterval]);

  const createGame = async () => {
    if (!hostName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    if (winPatterns.length === 0) {
      toast.error('Please select at least one win pattern');
      return;
    }
    
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/bingo/games`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          host_name: hostName,
          win_pattern: winPatterns[0], // Primary pattern
          win_patterns: winPatterns, // All selected patterns
          ticket_count: ticketCount,
          auto_call: false,
          auto_call_interval: autoCallInterval,
          club_id: clubId || null,
          game_type: 'uk90'
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Game created!');
        navigate(`/bingo/uk90/host/${data.game_code}`);
      } else {
        toast.error('Failed to create game');
      }
    } catch (error) {
      toast.error('Failed to create game');
    } finally {
      setCreating(false);
    }
  };

  const startGame = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${game.game_code}/start`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Game started!');
        fetchGame();
      }
    } catch (error) {
      toast.error('Failed to start game');
    }
  };

  const advancePattern = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${game.game_code}/next-pattern`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchGame();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Cannot advance pattern');
      }
    } catch (error) {
      toast.error('Failed to advance pattern');
    }
  };

  const awardPatternWin = async (playerId) => {
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${game.game_code}/award-pattern`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: playerId })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchGame();
      }
    } catch (error) {
      toast.error('Failed to award win');
    }
  };

  const callNumber = async () => {
    if (calling) return;
    setCalling(true);
    
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${game.game_code}/call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      
      if (res.ok) {
        const data = await res.json();
        setLastCalledNumber(data.called_number);
        
        // Speak the number with UK call (only if audio is enabled)
        if (audioEnabled && 'speechSynthesis' in window) {
          const call = UK_BINGO_CALLS[data.called_number] || '';
          const text = `${data.called_number}. ${call}`;
          const utterance = new SpeechSynthesisUtterance(text);
          utterance.rate = 0.8;
          speechSynthesis.speak(utterance);
        }
        
        fetchGame();
      }
    } catch (error) {
      toast.error('Failed to call number');
    } finally {
      setCalling(false);
    }
  };

  const endGame = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${game.game_code}/end`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Game ended');
        fetchGame();
      }
    } catch (error) {
      toast.error('Failed to end game');
    }
  };

  const resetGame = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${game.game_code}/reset`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Game reset for new round');
        setLastCalledNumber(null);
        setAutoCallActive(false);
        fetchGame();
      }
    } catch (error) {
      toast.error('Failed to reset game');
    }
  };

  const updateSettings = async (pattern) => {
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${game.game_code}/settings?win_pattern=${pattern}`, {
        method: 'POST'
      });
      if (res.ok) {
        fetchGame();
      }
    } catch (error) {
      console.error('Failed to update settings');
    }
  };

  const copyJoinLink = () => {
    const url = `${window.location.origin}/bingo/uk90/play/${game.game_code}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Join link copied!');
  };

  const joinUrl = game ? `${window.location.origin}/bingo/uk90/play/${game.game_code}` : '';

  // Create game form
  if (!gameCode) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-900/30 to-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Hash className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
              UK 90-Ball Bingo
            </h1>
            <p className="text-slate-400 mt-1">Traditional British bingo</p>
          </div>
          
          {/* Game Type Switcher */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            <Link to="/bingo/host" className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors">
              <Music className="w-4 h-4" />
              Line Dance
            </Link>
            <div className="bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
              <Hash className="w-4 h-4" />
              UK 90-Ball
            </div>
            <Link to="/raffle/host" className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors">
              <Ticket className="w-4 h-4" />
              Raffle
            </Link>
          </div>
          
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6 space-y-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Your Name (Host)</label>
                <Input
                  value={hostName}
                  onChange={(e) => setHostName(e.target.value)}
                  placeholder="Enter your name"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Win Patterns (select multiple for progressive play)</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'one_line', label: '1 Line', desc: 'Any row' },
                    { key: 'two_lines', label: '2 Lines', desc: 'Any 2 rows' },
                    { key: 'full_house', label: 'Full House', desc: 'All 15' }
                  ].map(pattern => (
                    <button
                      key={pattern.key}
                      onClick={() => {
                        if (winPatterns.includes(pattern.key)) {
                          // Remove if already selected (but keep at least one)
                          if (winPatterns.length > 1) {
                            setWinPatterns(winPatterns.filter(p => p !== pattern.key));
                          }
                        } else {
                          setWinPatterns([...winPatterns, pattern.key].sort((a, b) => {
                            const order = ['one_line', 'two_lines', 'full_house'];
                            return order.indexOf(a) - order.indexOf(b);
                          }));
                        }
                      }}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        winPatterns.includes(pattern.key)
                          ? 'border-amber-500 bg-amber-900/30' 
                          : 'border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <div className="text-white font-medium text-sm">{pattern.label}</div>
                      <div className="text-xs text-slate-400">{pattern.desc}</div>
                    </button>
                  ))}
                </div>
                {winPatterns.length > 1 && (
                  <p className="text-xs text-amber-400 mt-2">
                    Progressive: {winPatterns.map(p => p.replace('_', ' ')).join(' → ')}
                  </p>
                )}
              </div>
              
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Tickets Per Player</label>
                <div className="grid grid-cols-6 gap-2">
                  {[1, 2, 3, 4, 5, 6].map(num => (
                    <button
                      key={num}
                      onClick={() => setTicketCount(num)}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        ticketCount === num
                          ? 'border-amber-500 bg-amber-900/30' 
                          : 'border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <div className="text-white font-bold">{num}</div>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-1">No duplicate numbers across a player's tickets</p>
              </div>
              
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Auto-Call Speed (seconds)</label>
                <Input
                  type="number"
                  min="5"
                  max="60"
                  value={autoCallInterval}
                  onChange={(e) => setAutoCallInterval(parseInt(e.target.value) || 15)}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              {/* Club Selection - Show user's clubs if they have any with bingo enabled */}
              {userClubs.length > 0 ? (
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Your Club</label>
                  {userClubs.length === 1 ? (
                    <div className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white flex items-center">
                      {userClubs[0].name}
                    </div>
                  ) : (
                    <select
                      value={clubId}
                      onChange={(e) => setClubId(e.target.value)}
                      className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white"
                    >
                      {userClubs.map(club => (
                        <option key={club.club_id} value={club.club_id}>{club.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              ) : clubs.length > 0 && (
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Link to Club (optional)</label>
                  <select
                    value={clubId}
                    onChange={(e) => setClubId(e.target.value)}
                    className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white"
                  >
                    <option value="">No club - standalone game</option>
                    {clubs.map(club => (
                      <option key={club.club_id} value={club.club_id}>{club.name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <Button
                onClick={createGame}
                disabled={creating}
                className="w-full bg-amber-600 hover:bg-amber-500 h-12 text-lg"
              >
                {creating ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Play className="w-5 h-5 mr-2" />
                )}
                Create Game
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  // Game host dashboard
  return (
    <div className="min-h-screen bg-slate-950 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Hash className="w-6 h-6 text-amber-400" />
              UK 90-Ball Bingo
            </h1>
            <p className="text-slate-400">
              Host: {game.host_name}
              {game.club_name && <span className="ml-2 text-amber-400">• {game.club_name}</span>}
              {game.ticket_count > 1 && <span className="ml-2 text-slate-400">• {game.ticket_count} tickets/player</span>}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Audio Toggle */}
            <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-slate-800/50">
              <button
                onClick={() => setAudioEnabled(!audioEnabled)}
                className={`p-1.5 rounded transition-colors ${audioEnabled ? 'text-amber-400' : 'text-slate-400'}`}
                title={audioEnabled ? 'Audio On - Click to mute' : 'Audio Off - Click to enable'}
              >
                {audioEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </button>
            </div>
            <Badge className={`${
              game.status === 'waiting' ? 'bg-yellow-500' :
              game.status === 'active' ? 'bg-green-500' : 'bg-slate-500'
            }`}>
              {game.status === 'waiting' ? 'Waiting for players' :
               game.status === 'active' ? 'Game in progress' : 'Game ended'}
            </Badge>
            {/* Current Pattern Badge */}
            <Badge variant="outline" className="text-amber-400 border-amber-600">
              {game.win_patterns && game.win_patterns.length > 1 ? (
                <>
                  Playing for: {(game.win_patterns[game.current_pattern_index || 0] || 'one_line').replace('_', ' ')}
                  <span className="ml-1 text-slate-400">
                    ({(game.current_pattern_index || 0) + 1}/{game.win_patterns.length})
                  </span>
                </>
              ) : (
                game.win_pattern === 'one_line' ? '1 Line wins' : 
                game.win_pattern === 'two_lines' ? '2 Lines win' : 'Full House'
              )}
            </Badge>
          </div>
        </div>

        {/* Pattern Progress & Controls for Progressive Play */}
        {game.win_patterns && game.win_patterns.length > 1 && game.status === 'active' && (
          <div className="mb-4 bg-slate-800/50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-semibold mb-2">Progressive Play</h3>
                <div className="flex gap-2">
                  {game.win_patterns.map((pattern, idx) => {
                    const winner = game.pattern_winners?.[pattern];
                    const isCurrent = idx === (game.current_pattern_index || 0);
                    return (
                      <div
                        key={pattern}
                        className={`px-3 py-2 rounded-lg text-sm ${
                          winner ? 'bg-green-900/50 border border-green-600' :
                          isCurrent ? 'bg-amber-900/50 border border-amber-500' :
                          'bg-slate-700/50 border border-slate-600'
                        }`}
                      >
                        <div className={`font-medium ${isCurrent ? 'text-amber-400' : winner ? 'text-green-400' : 'text-slate-400'}`}>
                          {pattern.replace('_', ' ')}
                        </div>
                        {winner && (
                          <div className="text-xs text-green-300">{winner.nickname}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              {(game.current_pattern_index || 0) < game.win_patterns.length - 1 && (
                <Button
                  onClick={advancePattern}
                  className="bg-amber-600 hover:bg-amber-500"
                >
                  Next Pattern →
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Main Layout */}
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Left Side - QR Code & Controls */}
          <div className="lg:w-2/3 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* QR Code & Join Info */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg">Join Game</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-white p-4 rounded-lg w-fit mx-auto">
                    <QRCodeSVG value={joinUrl} size={150} />
                  </div>
                  
                  <div className="text-center">
                    <div className="text-3xl font-mono font-bold text-amber-400 tracking-wider">
                      {game.game_code}
                    </div>
                    <p className="text-sm text-slate-400 mt-1">Game Code</p>
                  </div>
                  
                  <Button
                    variant="outline"
                    onClick={copyJoinLink}
                    className="w-full border-slate-700 text-white"
                  >
                    {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    {copied ? 'Copied!' : 'Copy Join Link'}
                  </Button>
                  
                  <div className="flex items-center gap-2 text-slate-400">
                    <Users className="w-4 h-4" />
                    <span>{game.players?.length || 0} players joined</span>
                  </div>
                </CardContent>
              </Card>

              {/* Game Controls */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg">Game Controls</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Winner Banner */}
                  {game.winners?.length > 0 && (
                    <div className="bg-gradient-to-r from-amber-600 to-yellow-500 rounded-lg p-4 text-center animate-pulse">
                      <Crown className="w-8 h-8 mx-auto text-white mb-2" />
                      <div className="text-xl font-bold text-white">
                        {game.winners[game.winners.length - 1].nickname} WINS!
                      </div>
                      <div className="text-sm text-white/80">
                        {game.winners[game.winners.length - 1].pattern === 'one_line' ? '1 Line' :
                         game.winners[game.winners.length - 1].pattern === 'two_lines' ? '2 Lines' : 'Full House'}
                      </div>
                    </div>
                  )}

                  {/* Current Number */}
                  {lastCalledNumber && game.status === 'active' && (
                    <div className="bg-amber-900/50 rounded-lg p-4 text-center">
                      <div className="text-sm text-amber-300 mb-1">Current Number</div>
                      <div className="text-5xl font-bold text-white">{lastCalledNumber}</div>
                      <div className="text-sm text-amber-300 mt-1">
                        {UK_BINGO_CALLS[lastCalledNumber] || ''}
                      </div>
                    </div>
                  )}

                  {/* Control Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    {game.status === 'waiting' && (
                      <Button
                        onClick={startGame}
                        disabled={!game.players?.length}
                        className="col-span-2 bg-green-600 hover:bg-green-500 h-12"
                      >
                        <Play className="w-5 h-5 mr-2" />
                        Start Game ({game.players?.length || 0} players)
                      </Button>
                    )}
                    
                    {game.status === 'active' && (
                      <>
                        <Button
                          onClick={callNumber}
                          disabled={calling}
                          className="bg-amber-600 hover:bg-amber-500 h-12"
                        >
                          {calling ? (
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                          ) : (
                            <Volume2 className="w-5 h-5 mr-2" />
                          )}
                          Call
                        </Button>
                        
                        <Button
                          onClick={() => setAutoCallActive(!autoCallActive)}
                          className={`h-12 ${
                            autoCallActive 
                              ? 'bg-slate-600 hover:bg-slate-500' 
                              : 'bg-blue-600 hover:bg-blue-500'
                          }`}
                        >
                          {autoCallActive ? (
                            <><Pause className="w-4 h-4 mr-1" /> Stop</>
                          ) : (
                            <><Play className="w-4 h-4 mr-1" /> Auto</>
                          )}
                        </Button>
                      </>
                    )}
                    
                    <Button
                      onClick={resetGame}
                      variant="outline"
                      className="border-slate-700 text-white hover:bg-slate-800"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reset
                    </Button>
                    
                    <Button
                      onClick={endGame}
                      variant="outline"
                      className="border-red-700 text-red-400 hover:bg-red-900/20"
                    >
                      <Square className="w-4 h-4 mr-1" />
                      End
                    </Button>
                  </div>

                  {/* Settings */}
                  {game.status === 'waiting' && (
                    <div className="pt-3 border-t border-slate-800">
                      <div className="flex items-center gap-2 text-slate-400 mb-2">
                        <Settings className="w-4 h-4" />
                        <span className="text-sm">Win Pattern</span>
                      </div>
                      <div className="grid grid-cols-3 gap-1">
                        <button
                          onClick={() => updateSettings('one_line')}
                          className={`p-2 rounded border text-xs ${
                            game.win_pattern === 'one_line'
                              ? 'border-amber-500 bg-amber-900/30 text-white'
                              : 'border-slate-700 text-slate-400'
                          }`}
                        >
                          1 Line
                        </button>
                        <button
                          onClick={() => updateSettings('two_lines')}
                          className={`p-2 rounded border text-xs ${
                            game.win_pattern === 'two_lines'
                              ? 'border-amber-500 bg-amber-900/30 text-white'
                              : 'border-slate-700 text-slate-400'
                          }`}
                        >
                          2 Lines
                        </button>
                        <button
                          onClick={() => updateSettings('full_house')}
                          className={`p-2 rounded border text-xs ${
                            game.win_pattern === 'full_house'
                              ? 'border-amber-500 bg-amber-900/30 text-white'
                              : 'border-slate-700 text-slate-400'
                          }`}
                        >
                          Full House
                        </button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Called Numbers Grid */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg">
                  Called Numbers ({game.called_numbers?.length || 0}/90)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-10 gap-1">
                  {Array.from({ length: 90 }, (_, i) => i + 1).map((num) => {
                    const isCalled = game.called_numbers?.includes(num);
                    const isLast = num === lastCalledNumber;
                    return (
                      <div
                        key={num}
                        className={`
                          w-full aspect-square rounded flex items-center justify-center text-sm font-bold
                          ${isLast ? 'bg-amber-500 text-black animate-pulse' :
                            isCalled ? 'bg-amber-700 text-white' : 'bg-slate-800 text-slate-400'}
                        `}
                      >
                        {num}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Side - Leaderboard */}
          <div className="lg:w-1/3">
            <div className="lg:sticky lg:top-4">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-amber-400" />
                    Leaderboard
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {game.leaderboard && game.leaderboard.length > 0 ? (
                    <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                      {game.leaderboard.map((player, idx) => (
                        <div 
                          key={player.player_id}
                          className={`flex items-center justify-between p-2 rounded-lg ${
                            idx === 0 
                              ? 'bg-amber-900/30 border border-amber-500/50' 
                              : 'bg-slate-800/50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                              idx === 0 ? 'bg-yellow-500 text-black' :
                              idx === 1 ? 'bg-slate-400 text-black' :
                              idx === 2 ? 'bg-amber-700 text-white' :
                              'bg-slate-700 text-white'
                            }`}>
                              {idx + 1}
                            </div>
                            <span className="font-medium text-white">
                              {player.nickname}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <div className="text-sm font-bold text-white">
                                {player.complete_rows} row{player.complete_rows !== 1 ? 's' : ''}
                              </div>
                              <div className="text-xs text-slate-400">
                                {player.marked_count}/15
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Waiting for players...</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
